import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import {
  findServiceBySlug,
  findNeighborhoodByZip,
  priceRangeText,
  buildPageTitle,
  buildMetaDescription,
  buildCostPageJsonLd,
  buildPreMessage,
  costPageUrl,
} from "../../lib/seo/seoTargetMatrix";
import { STL_NEIGHBORHOODS, SEO_SERVICES } from "../../lib/seo/seoTargetMatrix";
import { CLUMSE_SEARCH_FEE_USD } from "../../lib/procurementPricing";

const SCOPE_LABEL_BY_TIER = {
  MINOR: "Repairs & service",
  MAJOR: "Major systems",
  CAPEX: "Large projects",
};

// ─── Styled components ────────────────────────────────────────────────────────

const Page = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    sans-serif;
  color: #1a1a2e;
  background: #fff;
  min-height: 100vh;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%);
  color: #fff;
  padding: 72px 24px 64px;
  text-align: center;
`;

const HeroEyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #ff7043;
  margin: 0 0 16px;
`;

const HeroH1 = styled.h1`
  font-size: clamp(26px, 5vw, 44px);
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 20px;
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;
`;

const HeroSubline = styled.p`
  font-size: clamp(16px, 2.5vw, 20px);
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 36px;
  max-width: 620px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
`;

const PriceRange = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 16px 28px;
  margin-bottom: 40px;
`;

const PriceLabel = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const PriceValue = styled.span`
  font-size: 28px;
  font-weight: 800;
  color: #fff;
`;

const CtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ff5722;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 16px 32px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  &:hover {
    background: #e64a19;
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
`;

const SearchFeeNote = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 14px 0 0;
`;

const Container = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Section = styled.section`
  padding: 56px 24px;
  border-bottom: 1px solid #f0f0f0;
  max-width: 860px;
  margin: 0 auto;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionLabel = styled.p`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #ff5722;
  margin: 0 0 8px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(20px, 3vw, 28px);
  font-weight: 700;
  margin: 0 0 28px;
  color: #1a1a2e;
`;

const AssessmentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

const AssessmentItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #f8f9fa;
  border: 1px solid #e8ecef;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 15px;
  line-height: 1.4;
  color: #2c3e50;

  &::before {
    content: "✓";
    color: #ff5722;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const TasksTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #e8ecef;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Td = styled.td`
  padding: 14px 16px;
  border: 1px solid #e8ecef;
  color: #2c3e50;
  vertical-align: top;
`;

const TrEven = styled.tr`
  background: #fafbfc;
`;

const HowItWorksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 24px;
`;

const HowStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StepNum = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ff5722;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepTitle = styled.p`
  font-weight: 600;
  font-size: 16px;
  margin: 0;
  color: #1a1a2e;
`;

const StepDesc = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  overflow: hidden;
`;

const FaqItem = styled.div`
  border-bottom: 1px solid #e8ecef;
  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 18px 20px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  line-height: 1.4;

  &:hover {
    background: #f8f9fa;
  }
`;

const FaqChevron = styled.span`
  flex-shrink: 0;
  font-size: 18px;
  color: #ff5722;
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
`;

const FaqAnswer = styled.div`
  display: ${({ $open }) => ($open ? "block" : "none")};
  padding: 4px 20px 20px;
  font-size: 15px;
  color: #4b5563;
  line-height: 1.65;
`;

const FooterCtaBox = styled.section`
  background: linear-gradient(135deg, #1a1a2e, #0f3460);
  color: #fff;
  text-align: center;
  padding: 64px 24px;
`;

const FooterCtaTitle = styled.h2`
  font-size: clamp(22px, 3.5vw, 34px);
  font-weight: 700;
  margin: 0 0 14px;
`;

const FooterCtaSubline = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 32px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
`;

const TierBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: ${({ $tier }) =>
    $tier === "CAPEX" ? "#7c3aed" : $tier === "MAJOR" ? "#0f3460" : "#1a7a4a"};
  color: #fff;
  margin-left: 8px;
  vertical-align: middle;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const RelatedCard = styled(Link)`
  display: block;
  padding: 16px;
  border: 1px solid #e8ecef;
  border-radius: 8px;
  text-decoration: none;
  color: #1a1a2e;
  font-size: 14px;
  font-weight: 500;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #ff5722;
    box-shadow: 0 2px 8px rgba(255, 87, 34, 0.12);
  }
`;

const RelatedCardTitle = styled.p`
  margin: 0 0 4px;
  font-weight: 600;
`;

const RelatedCardMeta = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 13px;
`;

// ─── FAQ Accordion ────────────────────────────────────────────────────────────

function FaqAccordion({ items }) {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <FaqList>
      {items.map((item, i) => (
        <FaqItem key={i}>
          <FaqQuestion onClick={() => setOpenIdx(openIdx === i ? null : i)}>
            {item.question}
            <FaqChevron $open={openIdx === i}>▾</FaqChevron>
          </FaqQuestion>
          <FaqAnswer $open={openIdx === i}>{item.answer}</FaqAnswer>
        </FaqItem>
      ))}
    </FaqList>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CostLandingPage() {
  const { serviceType, zipCode } = useParams();
  const navigate = useNavigate();

  const service = findServiceBySlug(serviceType);
  const neighborhood = findNeighborhoodByZip(zipCode);

  if (!service || !neighborhood) {
    return (
      <Page>
        <Helmet>
          <title>Not Found | Clumse</title>
        </Helmet>
        <Section style={{ textAlign: "center", paddingTop: 100 }}>
          <SectionTitle>Page not found</SectionTitle>
          <p style={{ color: "#6b7280" }}>
            We don't have a cost guide for that combination yet.
          </p>
          <Link to="/" style={{ color: "#ff5722", fontWeight: 600 }}>
            ← Back to Clumse
          </Link>
        </Section>
      </Page>
    );
  }

  const pageTitle = buildPageTitle(service, neighborhood);
  const metaDesc = buildMetaDescription(service, neighborhood);
  const jsonLd = buildCostPageJsonLd(service, neighborhood);
  const canonicalUrl = `https://clumse.com${costPageUrl(service.slug, neighborhood.zip)}`;
  const preMessage = buildPreMessage(service, neighborhood);
  const chatUrl = `/chat?preMessage=${encodeURIComponent(preMessage)}&zip=${neighborhood.zip}`;

  // Related guides: same service in nearby ZIPs + other services in this ZIP
  const nearbyZips = STL_NEIGHBORHOODS.filter((n) => n.zip !== neighborhood.zip).slice(0, 4);
  const relatedServices = SEO_SERVICES.filter(
    (s) => s.slug !== service.slug && s.tier === service.tier
  ).slice(0, 3);

  return (
    <Page>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDesc} />
        {jsonLd && (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )}
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <Hero>
        <HeroEyebrow>
          {neighborhood.name}, {neighborhood.region} · {neighborhood.zip}
        </HeroEyebrow>

        <HeroH1>
          Average Cost of {service.name} in {neighborhood.name}
          <TierBadge $tier={service.tier}>
            {SCOPE_LABEL_BY_TIER[service.tier] || "Residential"}
          </TierBadge>
        </HeroH1>

        <HeroSubline>
          Local market costs typically range from{" "}
          <strong>{priceRangeText(service)}</strong> based on recent St. Louis
          area benchmarks.
        </HeroSubline>

        <PriceRange>
          <PriceLabel>Typical range</PriceLabel>
          <PriceValue>{priceRangeText(service)}</PriceValue>
        </PriceRange>

        <br />

        <CtaButton onClick={() => navigate(chatUrl)}>
          {service.ctaLabel} →
        </CtaButton>

        <SearchFeeNote>
          ${CLUMSE_SEARCH_FEE_USD} search fee · 3 vetted bids · No commitment
        </SearchFeeNote>
      </Hero>

      {/* ── What contractors will assess ─────────────────────────── */}
      <Section>
        <SectionLabel>Assessment</SectionLabel>
        <SectionTitle>What Contractors Will Evaluate</SectionTitle>
        <p style={{ color: "#4b5563", marginBottom: 24, lineHeight: 1.6 }}>
          Every Clumse-matched contractor receives your pre-scoped brief before
          arriving. Here's what they'll assess on-site for a{" "}
          <strong>{service.name.toLowerCase()}</strong> project in{" "}
          {neighborhood.name}:
        </p>
        <AssessmentList>
          {service.assessmentPoints.map((point, i) => (
            <AssessmentItem key={i}>{point}</AssessmentItem>
          ))}
        </AssessmentList>
      </Section>

      {/* ── Pre-scoped task breakdown ─────────────────────────────── */}
      <Section>
        <SectionLabel>Scope of Work</SectionLabel>
        <SectionTitle>Pre-Scoped Work Items</SectionTitle>
        <p style={{ color: "#4b5563", marginBottom: 24, lineHeight: 1.6 }}>
          Clumse builds a technical scope package before reaching out to
          contractors. Below is the standard work breakdown for a{" "}
          {service.name.toLowerCase()} in the {neighborhood.name} area.
          Contractors bid against this scope - so you're comparing apples to
          apples.
        </p>
        <TasksTable>
          <thead>
            <tr>
              <Th style={{ width: "40%" }}>Work Item</Th>
              <Th>Detail</Th>
            </tr>
          </thead>
          <tbody>
            {service.tasks.map((task, i) => {
              const Row = i % 2 === 1 ? TrEven : "tr";
              return (
                <Row key={i}>
                  <Td style={{ fontWeight: 500 }}>{task.item}</Td>
                  <Td style={{ color: "#6b7280" }}>{task.detail}</Td>
                </Row>
              );
            })}
          </tbody>
        </TasksTable>
      </Section>

      {/* ── How Clumse works ─────────────────────────────────────── */}
      <Section>
        <SectionLabel>How it works</SectionLabel>
        <SectionTitle>Get 3 Competing Bids in 3 Steps</SectionTitle>
        <HowItWorksGrid>
          <HowStep>
            <StepNum>1</StepNum>
            <StepTitle>Describe your project</StepTitle>
            <StepDesc>
              Tell Clumse AI what needs to be done. The AI asks focused
              questions and builds a technical scope package - no paperwork, no
              forms.
            </StepDesc>
          </HowStep>
          <HowStep>
            <StepNum>2</StepNum>
            <StepTitle>Pay the ${CLUMSE_SEARCH_FEE_USD} search fee</StepTitle>
            <StepDesc>
              Clumse reaches out to vetted, licensed contractors in{" "}
              {neighborhood.zip} who specialize in {service.name.toLowerCase()}.
              The search fee covers the matching and outreach - nothing more.
            </StepDesc>
          </HowStep>
          <HowStep>
            <StepNum>3</StepNum>
            <StepTitle>Compare bids &amp; hire direct</StepTitle>
            <StepDesc>
              Receive detailed quotes from multiple contractors. Review, compare,
              and hire the one you choose. Clumse never takes a cut of the
              contractor's fee.
            </StepDesc>
          </HowStep>
        </HowItWorksGrid>
      </Section>

      {/* ── FAQ ──────────────────────────────────────────────────── */}
      <Section>
        <SectionLabel>FAQ</SectionLabel>
        <SectionTitle>
          Common Questions About {service.name} in {neighborhood.name}
        </SectionTitle>
        <FaqAccordion items={service.faq} />
      </Section>

      {/* ── Related guides ────────────────────────────────────────── */}
      {(nearbyZips.length > 0 || relatedServices.length > 0) && (
        <Section>
          <SectionLabel>Related Guides</SectionLabel>
          <SectionTitle>More Cost Guides</SectionTitle>
          <RelatedGrid>
            {nearbyZips.map((n) => (
              <RelatedCard key={n.zip} to={costPageUrl(service.slug, n.zip)}>
                <RelatedCardTitle>{service.name}</RelatedCardTitle>
                <RelatedCardMeta>
                  {n.name} · {n.zip}
                </RelatedCardMeta>
              </RelatedCard>
            ))}
            {relatedServices.map((s) => (
              <RelatedCard
                key={s.slug}
                to={costPageUrl(s.slug, neighborhood.zip)}
              >
                <RelatedCardTitle>{s.name}</RelatedCardTitle>
                <RelatedCardMeta>{neighborhood.name}</RelatedCardMeta>
              </RelatedCard>
            ))}
          </RelatedGrid>
        </Section>
      )}

      {/* ── Footer CTA ────────────────────────────────────────────── */}
      <FooterCtaBox>
        <FooterCtaTitle>
          Ready to Get {service.name} Quotes in {neighborhood.name}?
        </FooterCtaTitle>
        <FooterCtaSubline>
          {service.description} The ${CLUMSE_SEARCH_FEE_USD} search fee covers
          outreach to at least 3 vetted contractors in {neighborhood.zip}.
        </FooterCtaSubline>
        <CtaButton onClick={() => navigate(chatUrl)}>
          {service.ctaLabel} →
        </CtaButton>
        <SearchFeeNote style={{ color: "rgba(255,255,255,0.45)" }}>
          ${CLUMSE_SEARCH_FEE_USD} · No commitment · Cancel anytime before payment
        </SearchFeeNote>
      </FooterCtaBox>
    </Page>
  );
}
